<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { CoreTypes, type Dash, type Fill, type LinkRecord, type ObjectRecord, type Stroke, type Style, type TextStyle } from '@vp/shared-types';
import { DEFAULT_TEXT_STYLE, ulid } from '@vp/core';
import { live, ui } from '../editor/bridge';
import { allLocked, hasGroup, primary, propsDockedFor, selectedObjects, setPropsDockedFor, styleTargets, supportsFill, supportsStroke, supportsText } from '../editor/selection';
import { t, typeLabel } from '../i18n';
import { commandsFor, linkTypeLabel, linkTypesFor, moduleTypeLabel, runCommand, toolbarFor } from '../modules';
import { ICON_NAMES } from './Icon.vue';
import Icon from './Icon.vue';
import ColorPop from './ColorPop.vue';
import QuickFields from './QuickFields.vue';
import ExportDialog from './ExportDialog.vue';
import { FONTS, fontOf } from '../fonts';
import { BOOLEAN_OPS, booleanSupported } from '@vp/render';
import { keepCanvasSelection } from '../editor/dismiss';
import { armPainter, copyStyle, hasDefaultStyle, painter, pasteStyle, setDefaultStyle } from '../editor/style-painter';

/**
 * Top properties bar (§6.3): appears with a selection and shows only what applies to it.
 * Every chip opens a labelled drop-down. With a mixed selection each setting reads from and
 * writes to only the objects it applies to (fill never lands on a line); differing values show as "mixed".
 */
type Pop = 'object' | 'fill' | 'stroke' | 'text' | 'opacity' | 'module' | 'links' | 'more' | 'shapeops' | 'flow' | null;
/** `docked`: shown inside the right sidebar (vertical, wrapping) instead of floating over the canvas. */
const props = withDefaults(defineProps<{ docked?: boolean }>(), { docked: false });
const pop = ref<Pop>(null);
const exporting = ref<'png' | 'svg' | null>(null);
const wrap = ref<HTMLElement | null>(null);
/**
 * Where the bar fits: between the top-left and top-right pills, centered in the room left over;
 * when the bar is wider than that room it moves below the pills (narrow windows, long selections).
 */
const place = reactive({ left: 12, right: 12, room: 0, below: false });
const GAP = 10;
let placeObs: ResizeObserver | null = null;
function measure(): void {
  const el = wrap.value;
  const stage = el?.parentElement;
  if (!el || !stage) return;
  const sr = stage.getBoundingClientRect();
  const tl = stage.querySelector('.top-left')?.getBoundingClientRect();
  const tr = stage.querySelector('.top-right')?.getBoundingClientRect();
  const left = tl ? tl.right - sr.left + GAP : 12;
  const right = tr ? sr.right - tr.left + GAP : 12;
  const room = sr.width - left - right;
  const bar = el.querySelector('.propbar') as HTMLElement | null;
  const need = bar ? bar.scrollWidth + 2 : 0;
  place.below = need > room;
  ui.barBelow = place.below;
  place.left = place.below ? 12 : left;
  place.right = place.below ? 12 : right;
  place.room = place.below ? sr.width - 24 : room;
}
watch(wrap, (el) => {
  placeObs?.disconnect();
  if (!el || props.docked) {
    ui.barBelow = false;
    return;
  }
  const stage = el.parentElement;
  placeObs = new ResizeObserver(() => requestAnimationFrame(measure));
  for (const x of [stage, stage?.querySelector('.top-left'), stage?.querySelector('.top-right'), el.querySelector('.propbar')]) if (x) placeObs.observe(x);
  requestAnimationFrame(measure);
});
const wrapStyle = computed(() => ({ left: place.left + 'px', right: place.right + 'px', top: place.below ? '64px' : '12px', maxWidth: place.room > 0 ? place.room + 'px' : undefined }));

const one = computed<ObjectRecord | null>(() => (selectedObjects.value.length === 1 ? selectedObjects.value[0] : null));
// the properties live either in the top bar or in the right sidebar (propsDockedFor: the user's choice, a module's default)
const visible = computed(() => ui.selection.length > 0 && !ui.editingText && propsDockedFor(selectedObjects.value) === props.docked);
const ro = computed(() => ui.readOnly);

const isLine = (o: ObjectRecord) => o.type === CoreTypes.line || o.type === CoreTypes.connector;
const textOf = (o: ObjectRecord) => String(o.props['text'] ?? o.props['label'] ?? o.props['title'] ?? '');

const fillTargets = computed(() => styleTargets.value.filter(supportsFill));
const strokeTargets = computed(() => styleTargets.value.filter(supportsStroke));
const textTargets = computed(() => styleTargets.value.filter((o) => supportsText(o) && textOf(o).length > 0));
const lineTargets = computed(() => styleTargets.value.filter(isLine));
const connectorTargets = computed(() => lineTargets.value.filter((o) => o.type === CoreTypes.connector));

function differ<T>(xs: T[], key: (x: T) => string): boolean {
  if (xs.length < 2) return false;
  const k = key(xs[0]);
  return xs.some((x) => key(x) !== k);
}

const fill = computed<Fill>(() => fillTargets.value[0]?.style.fill ?? { kind: 'none' });
const fillMixed = computed(() => differ(fillTargets.value, (o) => JSON.stringify(o.style.fill)));
const stroke = computed<Stroke | null>(() => strokeTargets.value[0]?.style.stroke ?? null);
const strokeMixed = computed(() => differ(strokeTargets.value, (o) => JSON.stringify(o.style.stroke)));
const allLines = computed(() => strokeTargets.value.length > 0 && strokeTargets.value.every(isLine));
const textStyle = computed<TextStyle>(() => textTargets.value[0]?.style.text ?? DEFAULT_TEXT_STYLE);
const fontMixed = computed(() => differ(textTargets.value, (o) => fontOf(o.style.text?.fontFamily)?.id ?? ''));
const fontId = computed(() => fontOf(textStyle.value.fontFamily)?.id);
const sizeMixed = computed(() => differ(textTargets.value, (o) => String(o.style.text?.fontSize)));
const opacity = computed(() => styleTargets.value[0]?.style.opacity ?? 1);
const opacityMixed = computed(() => differ(styleTargets.value, (o) => String(o.style.opacity)));
const isModule = computed(() => !!one.value && !one.value.type.startsWith('core:'));
const isFrame = computed(() => one.value?.type === CoreTypes.frame);
const title = computed(() => (one.value ? one.value.name || typeLabel(one.value.type, moduleTypeLabel) : t('props.multiple', { n: selectedObjects.value.length })));
const typeIcon = computed(() => (one.value?.type.startsWith('core:') ? one.value.type.slice(5) : 'module'));
const cmds = computed(() => commandsFor(selectedObjects.value));
/** Module buttons in the top bar (API 1.5): their commands, pressed state from the objects' props. */
const modButtons = computed(() => {
  void ui.docRev;
  return ro.value ? [] : toolbarFor(selectedObjects.value, ICON_NAMES);
});
const svgUrl = (svg: string) => 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
const links = computed<LinkRecord[]>(() => {
  void ui.docRev;
  return one.value && live.editor ? live.editor.doc.linksOf(one.value.id) : [];
});
const linkOptions = computed(() => (selectedObjects.value.length === 2 ? linkTypesFor(selectedObjects.value[0], selectedObjects.value[1]) : []));
const total = computed(() => styleTargets.value.length);

watch(() => ui.selection, () => (pop.value = null));

/** Shapes a combine operation can use: at least two outlines that are not locked. */
const boolCount = computed(() => selectedObjects.value.filter((o) => !o.locked && booleanSupported(o)).length);
const canBoolean = computed(() => boolCount.value >= 2);

/** Scheme direction: of a flowchart diagram (its header), or for shapes with arrows between them ("arrange"). */
const FLOW_DIRS = [
  ['down', 'dirDown'],
  ['right', 'dirRight'],
  ['up', 'dirUp'],
  ['left', 'dirLeft'],
] as const;
const diagramDir = computed(() => {
  void ui.docRev;
  return one.value && live.editor ? live.editor.diagramDirection(one.value.id) : null;
});
const canArrange = computed(() => {
  void ui.docRev;
  return !ro.value && selectedObjects.value.length > 1 && !!live.editor?.canArrange();
});
function pickDirection(dir: (typeof FLOW_DIRS)[number][0]): void {
  if (diagramDir.value && one.value) live.editor?.setDiagramDirection(one.value.id, dir);
  else live.editor?.arrangeFlow(dir);
}

function toggle(x: Exclude<Pop, null>): void {
  pop.value = pop.value === x ? null : x;
}

function onEsc(e: KeyboardEvent): void {
  if (e.key === 'Escape' && pop.value) {
    pop.value = null;
    e.stopPropagation();
  }
}
/** Close drop-downs on a press outside the bar and the open card. On the canvas that press only closes them. */
function onOutside(e: PointerEvent): void {
  if (!pop.value || !wrap.value) return;
  const t = e.target;
  if (t instanceof Node && wrap.value.contains(t)) {
    const el = t instanceof Element ? t : t.parentElement;
    if (el?.closest('.propbar, .drop')) return;
  }
  pop.value = null;
  keepCanvasSelection(e);
}
window.addEventListener('keydown', onEsc, true);
window.addEventListener('pointerdown', onOutside, true);
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onEsc, true);
  window.removeEventListener('pointerdown', onOutside, true);
  placeObs?.disconnect();
  ui.barBelow = false;
});

/** Apply a per-object style patch; objects the callback skips stay untouched. */
function each(fn: (o: ObjectRecord) => Partial<Style> | null): void {
  live.editor?.updateStyles(fn);
}

// ---- fill ----
const viewStep = () => live.editor?.viewScale ?? 1;
const round2 = (v: number) => parseFloat(v.toPrecision(2));
function isLight(hex: string): boolean {
  const n = parseInt(hex.slice(1, 7).padEnd(6, 'f'), 16);
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) > 200;
}

function convertFill(cur: Fill, kind: Fill['kind']): Fill {
  if (kind === 'none') return { kind: 'none' };
  if (kind === 'solid') {
    if (cur.kind === 'solid') return cur;
    return { kind: 'solid', color: cur.kind === 'none' ? '#ffffff' : cur.background ?? cur.color };
  }
  const prevPattern = cur.kind === 'lines' || cur.kind === 'grid' ? cur : null;
  // pattern lines take the shape's color unless it is too light to see; gaps stay transparent
  const color = prevPattern ? prevPattern.color : cur.kind === 'solid' && !isLight(cur.color) ? cur.color : '#64748b';
  const spacing = prevPattern?.spacing ?? round2(10 * viewStep());
  const base = { color, spacing, ...(prevPattern?.background ? { background: prevPattern.background } : {}) };
  return kind === 'lines' ? { kind, ...base, angle: Math.PI / 4 } : { kind, ...base };
}
function setFillKind(kind: Fill['kind']): void {
  each((o) => (supportsFill(o) ? { fill: convertFill(o.style.fill, kind) } : null));
}
function setFillColor(c: string | null): void {
  if (c === null) return setFillKind('none');
  each((o) => {
    if (!supportsFill(o)) return null;
    const f = o.style.fill;
    return { fill: f.kind === 'lines' || f.kind === 'grid' ? { ...f, color: c } : { kind: 'solid', color: c } };
  });
}
function setPatternGap(c: string | null): void {
  each((o) => {
    const f = o.style.fill;
    if (!supportsFill(o) || (f.kind !== 'lines' && f.kind !== 'grid')) return null;
    const { background: _drop, ...rest } = f;
    void _drop;
    return { fill: c ? { ...rest, background: c } : rest };
  });
}
const DENSITY = [
  ['densityDense', 6],
  ['densityMedium', 10],
  ['densitySparse', 18],
] as const;
function setDensity(px: number): void {
  const spacing = round2(px * viewStep());
  each((o) => {
    const f = o.style.fill;
    return supportsFill(o) && (f.kind === 'lines' || f.kind === 'grid') ? { fill: { ...f, spacing } } : null;
  });
}
const densityPx = computed(() => {
  const f = fill.value;
  return f.kind === 'lines' || f.kind === 'grid' ? (f.spacing ?? 10) / viewStep() : 0;
});
function densityActive(px: number): boolean {
  const cur = densityPx.value;
  const nearest = DENSITY.reduce((a, b) => (Math.abs(b[1] - cur) < Math.abs(a[1] - cur) ? b : a));
  return nearest[1] === px;
}
const fillSwatch = computed(() => {
  const f = fill.value;
  if (fillMixed.value) return { cls: 'mixed', style: {} };
  if (f.kind === 'none') return { cls: 'none', style: {} };
  if (f.kind === 'solid') return { cls: '', style: { background: f.color } };
  const bg = f.background ?? '#ffffff';
  const img =
    f.kind === 'lines'
      ? `repeating-linear-gradient(45deg, ${f.color} 0 1.5px, ${bg} 1.5px 5px)`
      : `linear-gradient(${f.color} 1.2px, transparent 1.2px) 0 0 / 5px 5px, linear-gradient(90deg, ${f.color} 1.2px, ${bg} 1.2px) 0 0 / 5px 5px`;
  return { cls: '', style: { background: img } };
});

// ---- stroke ----
const DEFAULT_STROKE: Stroke = { color: '#1f2937', width: 2, dash: 'solid' };
function setStrokeColor(c: string | null): void {
  each((o) => {
    if (!supportsStroke(o)) return null;
    if (c === null) return isLine(o) ? null : { stroke: null };
    return { stroke: { ...(o.style.stroke ?? { ...DEFAULT_STROKE, width: round2(2 * viewStep()) }), color: c } };
  });
}
function patchStroke(patch: Partial<Stroke>): void {
  each((o) => (supportsStroke(o) ? { stroke: { ...(o.style.stroke ?? { ...DEFAULT_STROKE, width: round2(2 * viewStep()) }), ...patch } } : null));
}
const WIDTHS = [1, 2, 4, 8];
function widthActive(px: number): boolean {
  return !!stroke.value && !strokeMixed.value && Math.abs(stroke.value.width / viewStep() - px) < 0.26;
}
function widthStep(delta: number): void {
  const w = stroke.value?.width ?? 2;
  const s = w >= 10 ? 2 : w >= 4 ? 1 : w >= 1 ? 0.5 : 0.1;
  patchStroke({ width: Math.min(1000, Math.max(0.1, round2(w + delta * s))) });
}

// ---- lines ----
type ArrowMode = 'none' | 'end' | 'start' | 'both';
const arrowMode = computed<ArrowMode>(() => {
  const o = lineTargets.value[0];
  if (!o) return 'none';
  const s = !!o.props['arrowStart'];
  const e = !!o.props['arrowEnd'];
  return s && e ? 'both' : s ? 'start' : e ? 'end' : 'none';
});
function setArrows(m: ArrowMode): void {
  live.editor?.setProps(
    lineTargets.value.map((o) => o.id),
    { arrowStart: m === 'start' || m === 'both', arrowEnd: m === 'end' || m === 'both' },
  );
}
const route = computed(() => {
  const r = connectorTargets.value[0]?.props['route'];
  return r === 'straight' || r === 'curved' ? r : 'orthogonal';
});
function setRoute(r: 'straight' | 'orthogonal' | 'curved'): void {
  live.editor?.setProps(
    connectorTargets.value.map((o) => o.id),
    { route: r },
  );
}

// ---- text ----
function patchText(patch: Partial<TextStyle>): void {
  each((o) => (supportsText(o) ? { text: { ...(o.style.text ?? DEFAULT_TEXT_STYLE), ...patch } } : null));
}
const FONT_STEPS = [6, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 64, 80, 96, 128, 160, 200];
function nextSize(cur: number, delta: number): number {
  // the usual size ladder; beyond it (text made far zoomed in or out) step by a quarter
  const max = FONT_STEPS[FONT_STEPS.length - 1];
  if (cur >= max - 0.01 && delta > 0) return round2(cur * 1.25);
  if (cur > max + 0.01 && delta < 0) return Math.max(max, round2(cur / 1.25));
  if (cur <= FONT_STEPS[0] + 0.01 && delta < 0) return round2(cur / 1.25);
  if (cur < FONT_STEPS[0] - 0.01 && delta > 0) return Math.min(FONT_STEPS[0], round2(cur * 1.25));
  if (delta > 0) return FONT_STEPS.find((s) => s > cur + 0.01) ?? max;
  return [...FONT_STEPS].reverse().find((s) => s < cur - 0.01) ?? FONT_STEPS[0];
}
function fontStep(delta: number): void {
  each((o) => (supportsText(o) ? { text: { ...(o.style.text ?? DEFAULT_TEXT_STYLE), fontSize: nextSize((o.style.text ?? DEFAULT_TEXT_STYLE).fontSize, delta) } } : null));
}
const fontPx = computed(() => +textStyle.value.fontSize.toFixed(1));

// ---- object ----
function setProp(key: string, v: unknown): void {
  if (one.value) live.editor?.setProps([one.value.id], { [key]: v });
}
function setGeom(key: 'x' | 'y' | 'w' | 'h' | 'rotation', v: number): void {
  const o = one.value;
  if (!o || !Number.isFinite(v)) return;
  const val = key === 'rotation' ? (v * Math.PI) / 180 : key === 'w' || key === 'h' ? Math.max(1, v) : v;
  const patch: Partial<ObjectRecord> = { [key]: val };
  // proportions kept: the other side follows
  if (o.keepRatio && o.w > 0 && o.h > 0) {
    if (key === 'w') patch.h = (val * o.h) / o.w;
    if (key === 'h') patch.w = (val * o.w) / o.h;
  }
  live.editor?.updateObjects([{ id: o.id, patch }], 'geometry');
}
const keepRatio = computed(() => selectedObjects.value.length > 0 && selectedObjects.value.every((o) => o.keepRatio === true));
const notesCount = computed(() => (one.value ? (one.value.comments?.length ?? 0) + (one.value.reactions?.length ?? 0) : 0));
function styleCopied(): void {
  copyStyle();
  pop.value = null;
}
function setName(v: string): void {
  if (one.value) live.editor?.updateObjects([{ id: one.value.id, patch: { name: v.trim() || undefined } as Partial<ObjectRecord> }], 'rename');
}
function createLink(type: string): void {
  const [a, b] = selectedObjects.value;
  live.editor?.edit('link', (tx) => tx.createLink({ id: ulid(), canvasId: tx.document.canvasId, type, from: a.id, to: b.id, directed: true, formatVersion: 1, props: {} }));
}
function removeLink(id: string): void {
  live.editor?.edit('unlink', (tx) => tx.deleteLink(id));
}
function otherEnd(l: LinkRecord): string {
  const o = live.editor?.doc.get(l.from === one.value?.id ? l.to : l.from);
  return o ? o.name || typeLabel(o.type, moduleTypeLabel) : '?';
}
function align(mode: 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom' | 'distributeX' | 'distributeY'): void {
  live.editor?.alignSelection(mode);
}
const ALIGNS = [
  ['alignLeft', 'left'],
  ['alignCenterX', 'centerX'],
  ['alignRight', 'right'],
  ['alignTop', 'top'],
  ['alignCenterY', 'centerY'],
  ['alignBottom', 'bottom'],
] as const;
const round = (v: number) => Math.round(v * 100) / 100;
const DASHES: Dash[] = ['solid', 'dashed', 'fine', 'dotted'];
const dashArray: Record<Dash, string> = { solid: '', dashed: '5 3', fine: '2 2', dotted: '1 3' };
</script>

<template>
  <div v-if="visible" ref="wrap" class="propbar-wrap" :class="{ below: place.below && !docked, docked }" :style="docked ? undefined : wrapStyle" @pointerdown.stop @keydown.stop>
    <div class="propbar" :class="docked ? 'docked-bar' : 'float pillbar'" role="toolbar" :aria-label="t('props.title')">
      <button v-if="one && !ro" class="chip name" :class="{ on: pop === 'object' }" :title="t('props.object')" @click="toggle('object')">
        <Icon :name="typeIcon" :size="15" />
        <span class="name-text">{{ title }}</span>
        <Icon name="chevronDown" :size="13" />
      </button>
      <span v-else-if="!docked" class="name static"><span class="name-text">{{ title }}</span></span>
      <span class="sep"></span>
      <template v-if="modButtons.length">
        <button
          v-for="b in modButtons"
          :key="b.moduleId + b.command"
          class="chip mod-btn"
          :class="{ on: b.active }"
          :title="b.label"
          :aria-label="b.label"
          :aria-pressed="b.active"
          :data-command="b.moduleId + ':' + b.command"
          @click="runCommand(b.moduleId, b.command)"
        >
          <img v-if="b.svg" class="mod-icon" :src="svgUrl(b.svg)" alt="" />
          <Icon v-else :name="b.icon ?? 'module'" :size="15" />
        </button>
        <span class="sep"></span>
      </template>
      <QuickFields :objs="selectedObjects" />

      <template v-if="!ro">
        <button v-if="fillTargets.length" class="chip" :class="{ on: pop === 'fill' }" :title="t('props.fill')" :aria-label="t('props.fill')" @click="toggle('fill')">
          <span class="swatch-btn" :class="fillSwatch.cls" :style="fillSwatch.style"></span>
          <span class="cap">{{ t('props.fill') }}</span>
        </button>
        <button v-if="strokeTargets.length" class="chip" :class="{ on: pop === 'stroke' }" :title="allLines ? t('props.line') : t('props.contour')" :aria-label="allLines ? t('props.line') : t('props.contour')" @click="toggle('stroke')">
          <span class="swatch-btn ring" :class="{ none: !stroke && !strokeMixed, mixed: strokeMixed }" :style="stroke && !strokeMixed ? { borderColor: stroke.color } : {}"></span>
          <span class="cap">{{ allLines ? t('props.line') : t('props.contour') }}</span>
        </button>
        <button v-if="textTargets.length" class="chip" :class="{ on: pop === 'text' }" :title="t('props.text')" :aria-label="t('props.text')" @click="toggle('text')">
          <span class="text-a" :style="{ borderColor: textStyle.color }">Aa</span>
          <span class="num">{{ sizeMixed ? '—' : fontPx }}</span>
        </button>
        <button v-if="one && isLine(one) && !one.locked" class="chip" :title="t('props.labelHint')" @click="live.editor?.startTextEdit(one.id)">
          <Icon name="text" :size="15" />
          <span class="cap">{{ String(one.props['label'] ?? '') ? t('props.labelEdit') : t('props.labelAdd') }}</span>
        </button>
        <button v-if="one && one.type === 'core:diagram'" class="chip" :title="t('diagram.codeHint')" @click="ui.diagramEdit = { id: one.id, source: String(one.props['source'] ?? '') }">
          <Icon name="code" :size="15" />
          <span class="cap">{{ t('diagram.code') }}</span>
        </button>
        <button v-if="(diagramDir && !one?.locked) || canArrange" class="chip" :class="{ on: pop === 'flow' }" :title="diagramDir ? t('flow.directionHint') : t('flow.arrangeHint')" :aria-expanded="pop === 'flow'" data-act="flow" @click="toggle('flow')">
          <Icon :name="diagramDir ? FLOW_DIRS.find((d) => d[0] === diagramDir)![1] : 'diagram'" :size="15" />
          <span class="cap">{{ diagramDir ? t('flow.direction') : t('flow.arrange') }}</span>
        </button>
        <button v-if="one && one.type === 'core:path'" class="chip" :class="{ on: ui.pathEditing === one.id }" :aria-pressed="ui.pathEditing === one.id" :title="t('props.editPointsHint')" @click="ui.pathEditing === one.id ? live.editor?.exitPathEdit() : live.editor?.enterPathEdit(one.id)">
          <Icon name="path" :size="15" />
          <span class="cap">{{ t('props.editPoints') }}</span>
        </button>
        <button v-if="isFrame && one" class="chip" :class="{ on: one.props['clip'] !== false }" :aria-pressed="one.props['clip'] !== false" :title="t('props.clipContent')" @click="setProp('clip', one.props['clip'] === false)">
          <Icon name="crop" :size="15" />
          <span class="cap">{{ t('props.clipContent') }}</span>
        </button>
        <button v-if="canBoolean" class="chip" :class="{ on: pop === 'shapeops' }" :title="t('boolean.hint')" :aria-label="t('boolean.title')" :aria-expanded="pop === 'shapeops'" @click="toggle('shapeops')">
          <Icon name="shapeOps" :size="15" />
          <span class="cap">{{ t('boolean.title') }}</span>
        </button>
        <button class="chip" :class="{ on: pop === 'opacity' }" :title="t('props.opacity')" :aria-label="t('props.opacity')" @click="toggle('opacity')">
          <Icon name="opacity" :size="15" />
          <span class="num">{{ opacityMixed ? '—' : Math.round(opacity * 100) + '%' }}</span>
        </button>
        <!-- module settings live in the sidebar: the chip moves the properties there -->
        <button v-if="isModule && !docked" class="chip" :title="t('props.moduleProps')" data-act="module-props" @click="setPropsDockedFor(selectedObjects, true)"><Icon name="sliders" :size="15" /><span class="cap">{{ t('props.module') }}</span></button>
        <button v-if="links.length || linkOptions.length" class="chip" :class="{ on: pop === 'links' }" :title="t('props.links')" @click="toggle('links')">
          <Icon name="link" :size="15" /><span v-if="links.length" class="num">{{ links.length }}</span>
        </button>
        <span class="sep"></span>
        <button v-if="primary" class="icon-btn" :class="{ active: painter.armed }" :aria-label="t('palette.brush')" :title="t('palette.brushTitle')" :aria-pressed="painter.armed" data-act="style-brush" @click="armPainter()"><Icon name="brush" :size="16" /></button>
        <button v-if="one" class="icon-btn" :class="{ active: ui.linkFor === one.id || !!one.link }" :aria-label="t('links.title')" :title="t('links.title')" data-act="link" @click="ui.linkFor = ui.linkFor === one.id ? null : one.id"><Icon name="hyperlink" :size="16" /></button>
        <button v-if="one" class="icon-btn notes-btn" :class="{ active: ui.notesFor === one.id }" :aria-label="t('notes.open')" :title="t('notes.open') + ' (Ctrl+Alt+M)'" data-act="notes" @click="ui.notesFor = ui.notesFor === one.id ? null : one.id">
          <Icon name="comment" :size="16" /><span v-if="notesCount" class="dot-count">{{ notesCount }}</span>
        </button>
        <button class="icon-btn" :aria-label="t('cmd.duplicate')" :title="t('cmd.duplicate')" @click="live.editor?.duplicateSelection()"><Icon name="copy" :size="16" /></button>
        <button class="icon-btn" :class="{ active: allLocked }" :aria-label="allLocked ? t('cmd.unlock') : t('cmd.lock')" :title="allLocked ? t('cmd.unlock') : t('cmd.lock')" @click="live.editor?.toggleLock()">
          <Icon :name="allLocked ? 'lock' : 'unlock'" :size="16" />
        </button>
        <button class="icon-btn" :aria-label="t('cmd.delete')" :title="t('cmd.delete')" @click="live.editor?.deleteSelection()"><Icon name="trash" :size="16" /></button>
        <button class="icon-btn" :class="{ active: pop === 'more' }" :aria-label="t('props.arrange')" :title="t('props.arrange')" :aria-expanded="pop === 'more'" @click="toggle('more')"><Icon name="dots" :size="16" /></button>
      </template>
      <template v-if="!docked">
        <span class="sep"></span>
        <button class="icon-btn" :aria-label="t('props.dockSide')" :title="t('props.dockSide')" data-act="dock-side" @click="setPropsDockedFor(selectedObjects, true)"><Icon name="dockSide" :size="16" /></button>
      </template>
    </div>

    <!-- drop-downs -->
    <div v-if="pop && !ro" class="drop" :class="[pop, docked ? 'docked-drop' : 'float card']" role="dialog" :aria-label="pop" data-vp-dismiss>
      <!-- fill -->
      <template v-if="pop === 'fill'">
        <header>
          <strong>{{ t('props.fill') }}</strong>
          <span v-if="fillTargets.length < total" class="hint">{{ t('props.applyHint', { n: fillTargets.length, total }) }}</span>
        </header>
        <div class="tiles" role="radiogroup" :aria-label="t('props.fillType')">
          <button
            v-for="k in ['none', 'solid', 'lines', 'grid'] as const"
            :key="k"
            role="radio"
            class="tile"
            :class="{ active: !fillMixed && fill.kind === k }"
            :aria-checked="!fillMixed && fill.kind === k"
            @click="setFillKind(k)"
          >
            <span class="preview" :class="'pv-' + k"></span>
            <span>{{ t('props.fill' + k[0].toUpperCase() + k.slice(1)) }}</span>
          </button>
        </div>
        <template v-if="fill.kind === 'solid'">
          <p class="sec">{{ t('props.color') }}</p>
          <ColorPop class="inline" :value="fill.color" :mixed="fillMixed" @pick="setFillColor" @close="pop = null" />
        </template>
        <template v-else-if="fill.kind === 'lines' || fill.kind === 'grid'">
          <p class="sec">{{ t('props.patternColor') }}</p>
          <ColorPop class="inline" :value="fill.color" :mixed="fillMixed" @pick="setFillColor" @close="pop = null" />
          <p class="sec">{{ t('props.patternGap') }}</p>
          <ColorPop class="inline" :value="fill.background ?? null" allow-none :none-label="t('props.transparent')" :mixed="fillMixed" @pick="setPatternGap" @close="pop = null" />
          <p class="sec">{{ t('props.density') }}</p>
          <div class="seg-pills" role="radiogroup" :aria-label="t('props.density')">
            <button v-for="[label, px] in DENSITY" :key="px" role="radio" :aria-checked="densityActive(px)" :class="{ active: densityActive(px) }" @click="setDensity(px)">{{ t('props.' + label) }}</button>
          </div>
        </template>
      </template>

      <!-- outline / line -->
      <template v-else-if="pop === 'stroke'">
        <header>
          <strong>{{ allLines ? t('props.line') : t('props.contour') }}</strong>
          <span v-if="strokeTargets.length < total" class="hint">{{ t('props.applyHint', { n: strokeTargets.length, total }) }}</span>
        </header>
        <p class="sec">{{ t('props.color') }}</p>
        <ColorPop class="inline" :value="stroke?.color ?? null" :allow-none="!lineTargets.length" :none-label="t('props.noContour')" :mixed="strokeMixed" @pick="setStrokeColor" @close="pop = null" />
        <template v-if="stroke || strokeMixed">
          <p class="sec">{{ t('props.strokeWidth') }}</p>
          <div class="row">
            <div class="seg-pills" role="radiogroup" :aria-label="t('props.strokeWidth')">
              <button v-for="w in WIDTHS" :key="w" role="radio" :aria-checked="widthActive(w)" :class="{ active: widthActive(w) }" :title="w + ' px'" @click="patchStroke({ width: round2(w * viewStep()) })">
                <svg width="22" height="12" aria-hidden="true"><line x1="2" y1="6" x2="20" y2="6" stroke="currentColor" :stroke-width="Math.min(6, w)" stroke-linecap="round" /></svg>
              </button>
            </div>
            <div class="stepper">
              <button :aria-label="t('props.strokeWidth') + ' −'" @click="widthStep(-1)">−</button>
              <span class="num">{{ strokeMixed ? '—' : +(stroke?.width ?? 0).toFixed(1) }}</span>
              <button :aria-label="t('props.strokeWidth') + ' +'" @click="widthStep(1)">+</button>
            </div>
          </div>
          <p class="sec">{{ t('props.lineStyle') }}</p>
          <div class="seg-pills" role="radiogroup" :aria-label="t('props.dash')">
            <button v-for="d in DASHES" :key="d" role="radio" :aria-checked="!strokeMixed && stroke?.dash === d" :class="{ active: !strokeMixed && stroke?.dash === d }" :title="t('props.' + d)" @click="patchStroke({ dash: d })">
              <svg width="34" height="12" aria-hidden="true"><line x1="2" y1="6" x2="32" y2="6" stroke="currentColor" stroke-width="2" :stroke-dasharray="dashArray[d]" stroke-linecap="round" /></svg>
              <span class="lbl">{{ t('props.' + d) }}</span>
            </button>
          </div>
        </template>
        <template v-if="lineTargets.length">
          <p class="sec">{{ t('props.arrows') }}</p>
          <div class="seg-pills" role="radiogroup" :aria-label="t('props.arrows')">
            <button
              v-for="m in ['none', 'end', 'start', 'both'] as const"
              :key="m"
              role="radio"
              :aria-checked="arrowMode === m"
              :class="{ active: arrowMode === m }"
              :title="t(m === 'none' ? 'props.arrowNone' : m === 'end' ? 'props.arrowEnd' : m === 'start' ? 'props.arrowStart' : 'props.arrowBoth')"
              @click="setArrows(m)"
            >
              <svg width="34" height="12" aria-hidden="true">
                <line x1="4" y1="6" x2="30" y2="6" stroke="currentColor" stroke-width="1.6" />
                <path v-if="m === 'end' || m === 'both'" d="M25 2 L31 6 L25 10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
                <path v-if="m === 'start' || m === 'both'" d="M9 2 L3 6 L9 10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </template>
        <template v-if="connectorTargets.length">
          <p class="sec">{{ t('props.route') }}</p>
          <div class="seg-pills" role="radiogroup" :aria-label="t('props.route')">
            <button role="radio" :aria-checked="route === 'orthogonal'" :class="{ active: route === 'orthogonal' }" @click="setRoute('orthogonal')"><Icon name="connector" :size="14" />{{ t('props.routeOrthogonal') }}</button>
            <button role="radio" :aria-checked="route === 'curved'" :class="{ active: route === 'curved' }" @click="setRoute('curved')"><Icon name="path" :size="14" />{{ t('props.routeCurved') }}</button>
            <button role="radio" :aria-checked="route === 'straight'" :class="{ active: route === 'straight' }" @click="setRoute('straight')"><Icon name="line" :size="14" />{{ t('props.routeStraight') }}</button>
          </div>
        </template>
      </template>

      <!-- text -->
      <template v-else-if="pop === 'text'">
        <header>
          <strong>{{ t('props.text') }}</strong>
          <span v-if="textTargets.length < total" class="hint">{{ t('props.applyHint', { n: textTargets.length, total }) }}</span>
        </header>
        <p class="sec">{{ t('props.font') }}</p>
        <div class="fonts" role="radiogroup" :aria-label="t('props.font')">
          <button
            v-for="f in FONTS"
            :key="f.id"
            role="radio"
            class="font-pill"
            :aria-checked="!fontMixed && fontId === f.id"
            :class="{ active: !fontMixed && fontId === f.id }"
            :style="{ fontFamily: f.family }"
            :title="t(f.label + 'Hint')"
            @click="patchText({ fontFamily: f.family })"
          >
            <span class="sample">Аа</span>{{ t(f.label) }}
          </button>
        </div>
        <div class="cols">
          <div>
            <p class="sec">{{ t('props.size') }}</p>
            <div class="stepper big">
              <button :aria-label="t('props.fontSize') + ' −'" @click="fontStep(-1)">−</button>
              <span class="num">{{ sizeMixed ? '—' : fontPx }}</span>
              <button :aria-label="t('props.fontSize') + ' +'" @click="fontStep(1)">+</button>
            </div>
          </div>
          <div>
            <p class="sec">{{ t('props.fontStyle') }}</p>
            <div class="seg-pills">
              <button :class="{ active: !!textStyle.bold }" :aria-pressed="!!textStyle.bold" :title="t('props.bold')" :aria-label="t('props.bold')" @click="patchText({ bold: !textStyle.bold })"><Icon name="bold" :size="14" /></button>
              <button :class="{ active: !!textStyle.italic }" :aria-pressed="!!textStyle.italic" :title="t('props.italic')" :aria-label="t('props.italic')" @click="patchText({ italic: !textStyle.italic })"><Icon name="italic" :size="14" /></button>
            </div>
          </div>
        </div>
        <p class="sec">{{ t('props.color') }}</p>
        <ColorPop class="inline" :value="textStyle.color" :mixed="differ(textTargets, (o) => o.style.text?.color ?? '')" @pick="(c) => c && patchText({ color: c })" @close="pop = null" />
        <div class="cols">
          <div>
            <p class="sec">{{ t('props.hAlign') }}</p>
            <div class="seg-pills" role="radiogroup" :aria-label="t('props.hAlign')">
              <button v-for="a in ['left', 'center', 'right'] as const" :key="a" role="radio" :aria-checked="textStyle.align === a" :class="{ active: textStyle.align === a }" :aria-label="a" @click="patchText({ align: a })">
                <Icon :name="a === 'left' ? 'textLeft' : a === 'center' ? 'textCenter' : 'textRight'" :size="14" />
              </button>
            </div>
          </div>
          <div>
            <p class="sec">{{ t('props.vAlign') }}</p>
            <div class="seg-pills" role="radiogroup" :aria-label="t('props.vAlign')">
              <button v-for="a in ['top', 'middle', 'bottom'] as const" :key="a" role="radio" :aria-checked="textStyle.valign === a" :class="{ active: textStyle.valign === a }" :title="t('props.' + a)" :aria-label="t('props.' + a)" @click="patchText({ valign: a })">
                <Icon :name="a === 'top' ? 'vTop' : a === 'middle' ? 'vMiddle' : 'vBottom'" :size="14" />
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- combine shapes -->
      <template v-else-if="pop === 'shapeops'">
        <header>
          <strong>{{ t('boolean.title') }}</strong>
          <span v-if="boolCount < total" class="hint">{{ t('boolean.skipped', { n: boolCount, total }) }}</span>
        </header>
        <div class="ops" role="menu" :aria-label="t('boolean.title')">
          <button v-for="op in BOOLEAN_OPS" :key="op" role="menuitem" class="op" :title="t('boolean.' + op + 'Hint')" @click="(live.editor?.booleanOp(op), (pop = null))">
            <Icon :name="'op' + op[0].toUpperCase() + op.slice(1)" :size="22" />
            <span class="op-name">{{ t('boolean.' + op) }}</span>
            <kbd v-if="op !== 'divide'">{{ t('boolean.' + op + 'Key') }}</kbd>
          </button>
        </div>
        <p class="note">{{ t('boolean.note') }}</p>
      </template>

      <!-- scheme direction: a diagram's header, or arranging hand-drawn shapes -->
      <template v-else-if="pop === 'flow'">
        <header><strong>{{ diagramDir ? t('flow.direction') : t('flow.arrange') }}</strong></header>
        <div class="seg-pills" :role="diagramDir ? 'radiogroup' : 'group'" :aria-label="t('flow.direction')">
          <button
            v-for="[dir, icon] in FLOW_DIRS"
            :key="dir"
            :role="diagramDir ? 'radio' : undefined"
            :aria-checked="diagramDir ? diagramDir === dir : undefined"
            :class="{ active: diagramDir === dir }"
            :data-dir="dir"
            @click="pickDirection(dir)"
          >
            <Icon :name="icon" :size="14" />{{ t('flow.' + dir) }}
          </button>
        </div>
        <p class="note">{{ diagramDir ? t('flow.directionNote') : t('flow.arrangeNote') }}</p>
      </template>

      <!-- opacity -->
      <template v-else-if="pop === 'opacity'">
        <header><strong>{{ t('props.opacity') }}</strong></header>
        <div class="row">
          <input type="range" min="0.05" max="1" step="0.05" :value="opacity" :aria-label="t('props.opacity')" @change="live.editor?.setStyle({ opacity: +($event.target as HTMLInputElement).value })" />
          <span class="num">{{ opacityMixed ? '—' : Math.round(opacity * 100) + '%' }}</span>
        </div>
        <div class="seg-pills">
          <button v-for="v in [0.25, 0.5, 0.75, 1]" :key="v" :class="{ active: !opacityMixed && Math.abs(opacity - v) < 0.01 }" @click="live.editor?.setStyle({ opacity: v })">{{ v * 100 }}%</button>
        </div>
      </template>

      <!-- object: name and geometry -->
      <template v-else-if="pop === 'object' && one">
        <header><strong>{{ typeLabel(one.type, moduleTypeLabel) }}</strong></header>
        <label class="field-col">
          <span class="sec">{{ t('props.name') }}</span>
          <input class="num-pill grow" :value="one.name ?? ''" :placeholder="typeLabel(one.type, moduleTypeLabel)" @change="setName(($event.target as HTMLInputElement).value)" />
        </label>
        <p class="sec">{{ t('props.position') }}</p>
        <div class="geom">
          <label>X <input type="number" class="num-pill" :value="round(one.x)" :disabled="one.locked" @change="setGeom('x', +($event.target as HTMLInputElement).value)" /></label>
          <label>Y <input type="number" class="num-pill" :value="round(one.y)" :disabled="one.locked" @change="setGeom('y', +($event.target as HTMLInputElement).value)" /></label>
          <template v-if="!isLine(one)">
            <label v-if="!isFrame" :title="t('props.rotation')">∠ <input type="number" class="num-pill" :value="round((one.rotation * 180) / Math.PI)" :disabled="one.locked" @change="setGeom('rotation', +($event.target as HTMLInputElement).value)" /></label>
            <label v-else></label>
            <label>W <input type="number" class="num-pill" :value="round(one.w)" :disabled="one.locked" @change="setGeom('w', +($event.target as HTMLInputElement).value)" /></label>
            <label>H <input type="number" class="num-pill" :value="round(one.h)" :disabled="one.locked" @change="setGeom('h', +($event.target as HTMLInputElement).value)" /></label>
          </template>
        </div>
        <button v-if="!isLine(one) && one.type !== 'core:diagram'" class="pill ratio" :class="{ active: keepRatio }" :aria-pressed="keepRatio" :disabled="one.locked" data-act="keep-ratio" @click="live.editor?.setKeepRatio(!keepRatio)">
          <Icon name="ratio" :size="14" />{{ t('palette.keepRatio') }}
        </button>
      </template>

      <template v-else-if="pop === 'links'">
        <header><strong>{{ t('props.links') }}</strong></header>
        <div class="pills">
          <span v-for="l in links" :key="l.id" class="pill link">
            {{ l.from === one?.id ? '→' : '←' }} {{ otherEnd(l) }} · {{ linkTypeLabel(l.type) }}
            <button class="x" :aria-label="t('props.removeLink')" @click="removeLink(l.id)"><Icon name="close" :size="11" /></button>
          </span>
          <button v-for="o in linkOptions" :key="o.type" class="pill ghost" @click="createLink(o.type)"><Icon name="link" :size="13" />{{ o.label }}</button>
        </div>
      </template>

      <!-- order, groups, alignment, module commands -->
      <template v-else-if="pop === 'more'">
        <header><strong>{{ t('props.arrange') }}</strong></header>
        <div class="pills">
          <button class="pill" @click="live.editor?.reorderSelection('front')"><Icon name="front" :size="14" />{{ t('cmd.front') }}</button>
          <button class="pill" @click="live.editor?.reorderSelection('back')"><Icon name="back" :size="14" />{{ t('cmd.back') }}</button>
          <button v-if="!hasGroup" class="pill" :disabled="ui.selection.length < 2" @click="live.editor?.groupSelection()"><Icon name="group" :size="14" />{{ t('cmd.group').split(' (')[0] }}</button>
          <button v-else class="pill" @click="live.editor?.ungroupSelection()"><Icon name="ungroup" :size="14" />{{ t('cmd.ungroup').split(' (')[0] }}</button>
          <button v-if="isFrame" class="pill" @click="live.editor?.unframeSelection()"><Icon name="frame" :size="14" />{{ t('cmd.unframe') }}</button>
        </div>
        <template v-if="one && one.type === 'core:diagram'">
          <p class="sec">{{ t('diagram.title') }}</p>
          <div class="pills">
            <button class="pill" @click="live.editor?.resetDiagramSize(one.id)"><Icon name="fit" :size="14" />{{ t('diagram.naturalSize') }}</button>
            <button class="pill" :title="t('diagram.explodeHint')" @click="live.editor?.explodeDiagram(one.id, one.name || t('diagram.title'))"><Icon name="ungroup" :size="14" />{{ t('diagram.explode') }}</button>
          </div>
        </template>
        <template v-if="ui.selection.length > 1">
          <p class="sec">{{ t('cmd.align') }}</p>
          <div class="pills">
            <button v-for="[icon, mode] in ALIGNS" :key="mode" class="pill icon" :title="t('cmd.' + icon)" :aria-label="t('cmd.' + icon)" @click="align(mode)"><Icon :name="icon" :size="15" /></button>
            <template v-if="ui.selection.length > 2">
              <button class="pill icon" :title="t('cmd.distributeX')" :aria-label="t('cmd.distributeX')" @click="align('distributeX')"><Icon name="distributeX" :size="15" /></button>
              <button class="pill icon" :title="t('cmd.distributeY')" :aria-label="t('cmd.distributeY')" @click="align('distributeY')"><Icon name="distributeY" :size="15" /></button>
            </template>
          </div>
        </template>
        <template v-if="primary">
          <p class="sec">{{ t('props.style') }}</p>
          <div class="pills">
            <button class="pill" data-act="copy-style" @click="styleCopied"><Icon name="brush" :size="14" />{{ t('palette.copyStyle') }}</button>
            <button v-if="painter.style" class="pill" data-act="paste-style" @click="pasteStyle()">{{ t('palette.pasteStyle') }}</button>
            <button class="pill" data-act="default-style" :title="t('palette.defaultStyleHint')" @click="setDefaultStyle(primary, true), (pop = null)"><Icon name="palette" :size="14" />{{ t('palette.defaultStyle') }}</button>
            <button v-if="hasDefaultStyle(primary.type)" class="pill" data-act="reset-default-style" @click="setDefaultStyle(primary, false), (pop = null)">{{ t('palette.resetDefaultStyle') }}</button>
            <button v-if="ui.selection.length > 1" class="pill" :class="{ active: keepRatio }" :aria-pressed="keepRatio" @click="live.editor?.setKeepRatio(!keepRatio)"><Icon name="ratio" :size="14" />{{ t('palette.keepRatio') }}</button>
          </div>
        </template>
        <p class="sec">{{ isFrame ? t('cmd.exportFrame') : t('cmd.exportSelection') }}</p>
        <div class="pills">
          <button class="pill" @click="(exporting = 'png'), (pop = null)"><Icon name="download" :size="14" />PNG</button>
          <button class="pill" @click="(exporting = 'svg'), (pop = null)"><Icon name="download" :size="14" />SVG</button>
        </div>
        <template v-if="cmds.length">
          <p class="sec">{{ t('tools.moduleTools') }}</p>
          <div class="pills">
            <button v-for="c in cmds" :key="c.moduleId + c.id" class="pill" @click="runCommand(c.moduleId, c.id)">{{ c.label }}</button>
          </div>
        </template>
      </template>
    </div>
    <Teleport to="body"><ExportDialog v-if="exporting" :format="exporting" initial-scope="selection" @close="exporting = null" /></Teleport>
  </div>
</template>

<style scoped>
.propbar-wrap {
  position: absolute;
  top: 12px;
  /* centered between the top-left and top-right pills (left/right set from their sizes) */
  left: 0;
  right: 0;
  margin: 0 auto;
  width: max-content;
  z-index: 24;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: calc(100% - 24px);
  /* the bar is wider than its card; the empty margin must not catch presses — they belong to the canvas */
  pointer-events: none;
}
.propbar-wrap > * {
  pointer-events: auto;
}
/* docked in the right sidebar: the bar wraps into rows, drop-downs open inline under it */
.propbar-wrap.docked {
  position: static;
  width: 100%;
  max-width: none;
  margin: 0;
  align-items: stretch;
  pointer-events: auto;
}
.propbar.docked-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  overflow: visible;
  padding: 0 0 6px;
}
.propbar.docked-bar .name.static,
.propbar.docked-bar > .chip.name {
  flex-basis: 100%;
  justify-content: flex-start;
}
/* in rows a separator after the name or at a row end only makes a stray line */
.propbar.docked-bar > .sep {
  display: none;
}
.drop.docked-drop {
  width: auto;
  max-width: none;
  margin: 4px 0 0;
  padding: 10px 0 4px;
  border-top: 1px solid var(--border);
}
.propbar {
  position: static;
  gap: 2px;
  padding: 4px 6px;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}
.name {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-2);
  max-width: 200px;
}
.name.static {
  padding: 0 8px;
  flex: none;
}
.name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  min-width: 32px;
  padding: 0 9px;
  border: none;
  background: none;
  border-radius: 999px;
  cursor: pointer;
  color: var(--text-2);
  flex: none;
  justify-content: center;
  font-size: 12px;
}
.mod-icon {
  width: 16px;
  height: 16px;
  display: block;
}
:root[data-theme='dark'] .mod-icon {
  filter: invert(0.9);
}
.chip:hover {
  background: var(--hover);
  color: var(--text);
}
.chip.on {
  background: var(--accent-soft);
  color: var(--accent-text);
}
.swatch-btn {
  pointer-events: none;
  width: 18px;
  height: 18px;
}
.swatch-btn.ring {
  background: var(--panel);
  border-width: 4px;
  border-color: #1f2937;
  /* the ring is drawn in the contour's own color: thin lines on both edges keep a dark one visible on a dark pill (and a light one on a light pill) */
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--text) 38%, transparent),
    inset 0 0 0 1px color-mix(in srgb, var(--text) 38%, transparent);
}
.swatch-btn.mixed,
.swatch-btn.ring.mixed {
  background: conic-gradient(#f87171, #facc15, #4ade80, #60a5fa, #c084fc, #f87171);
  border-color: transparent;
}
.swatch-btn.ring.mixed {
  -webkit-mask: radial-gradient(circle, transparent 4px, #000 4.5px);
  mask: radial-gradient(circle, transparent 4px, #000 4.5px);
}
.num {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.text-a {
  font-weight: 600;
  font-size: 13px;
  border-bottom: 3px solid;
  line-height: 13px;
  padding: 0 1px;
}
.stepper {
  display: inline-flex;
  align-items: center;
  height: 28px;
  background: var(--hover);
  border-radius: 999px;
  padding: 0 2px;
  flex: none;
}
.stepper.big {
  height: 30px;
}
.stepper button {
  border: none;
  background: none;
  height: 24px;
  min-width: 26px;
  border-radius: 999px;
  cursor: pointer;
  color: var(--text-2);
  font-size: 15px;
}
.stepper button:hover {
  background: var(--panel);
}
.stepper .num {
  min-width: 34px;
  text-align: center;
}
.propbar .icon-btn {
  width: 32px;
  height: 32px;
  flex: none;
}
/* docked: a gray disc under each action, same track gray as segmented controls on a white card */
.propbar.docked-bar .icon-btn {
  background: var(--seg);
}
.propbar.docked-bar .icon-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--text) 8%, var(--seg));
  color: var(--text);
}
.propbar.docked-bar .icon-btn.active {
  background: var(--accent-soft);
  color: var(--accent-text);
}
/* chip captions only where there is room for them */
@media (max-width: 1180px) {
  .cap {
    display: none;
  }
}
.drop {
  position: static;
  margin-top: 8px;
  padding: 12px 14px 14px;
  width: max-content;
  max-width: min(360px, calc(100vw - 24px));
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 25;
}
.drop header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 2px;
}
.drop header strong {
  font-size: 13px;
  font-weight: 600;
}
.hint {
  font-size: 11px;
  color: var(--text-3);
}
.sec {
  margin: 6px 0 0;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-3);
}
.drop :deep(.colorpop.inline),
.drop .inline {
  position: static;
  box-shadow: none;
  border: none;
  padding: 0;
  background: none;
  backdrop-filter: none;
}
.row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cols {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}
.pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pill.icon {
  padding: 0 8px;
}
.seg-pills button {
  gap: 6px;
}
.seg-pills .lbl {
  font-size: 11.5px;
}
.tiles {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 8px 4px 6px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: var(--panel-2);
  color: var(--text-2);
  font-size: 11.5px;
  cursor: pointer;
}
.tile:hover {
  background: var(--hover);
}
.tile.active {
  border-color: var(--accent-border);
  background: var(--accent-soft);
  color: var(--accent-text);
}
.preview {
  width: 30px;
  height: 22px;
  border-radius: 6px;
  border: 1.5px solid #94a3b8;
  background-color: #fff;
}
.pv-none {
  background: linear-gradient(135deg, #fff 45%, #ef4444 45%, #ef4444 55%, #fff 55%);
}
.pv-solid {
  background: #93c5fd;
}
.pv-lines {
  background: repeating-linear-gradient(45deg, #64748b 0 1.2px, transparent 1.2px 5px);
}
.pv-grid {
  background:
    linear-gradient(#64748b 1px, transparent 1px) 0 0 / 5px 5px,
    linear-gradient(90deg, #64748b 1px, transparent 1px) 0 0 / 5px 5px;
}
.field-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.geom {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px 10px;
}
.geom label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-3);
}
.geom .num-pill {
  width: 70px;
}
.grow {
  width: 100%;
}
.link {
  cursor: default;
  padding-right: 4px;
}
.link .x {
  border: none;
  background: none;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  cursor: pointer;
  color: var(--text-3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
input[type='range'] {
  width: 200px;
}
.fonts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
}
.font-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 5px 8px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--panel);
  color: var(--text);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}
.font-pill:hover {
  background: var(--hover);
}
.font-pill.active {
  border-color: var(--accent);
  background: var(--accent-soft);
  color: var(--accent-text);
}
.font-pill .sample {
  font-size: 15px;
  width: 22px;
  flex: none;
}
.ops {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}
.op {
  display: grid;
  grid-template-columns: 26px 1fr;
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--panel);
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.op:hover {
  background: var(--hover);
  border-color: var(--accent-border);
}
.op :deep(svg) {
  grid-row: 1 / 3;
  color: var(--accent-text);
}
.op-name {
  font-size: 13px;
  font-weight: 500;
}
.op kbd {
  font: inherit;
  font-size: 10.5px;
  color: var(--text-3);
}
.note {
  margin: 8px 2px 0;
  font-size: 11.5px;
  line-height: 1.4;
  color: var(--text-3);
}
.notes-btn {
  position: relative;
}
.dot-count {
  position: absolute;
  top: 1px;
  right: 0;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 999px;
  background: var(--accent);
  color: var(--on-accent, #fff);
  font-size: 9.5px;
  line-height: 14px;
  text-align: center;
  box-sizing: border-box;
}
.pill.ratio {
  align-self: flex-start;
  margin-top: 8px;
}
</style>
